import { EmptyRowsMode, ReportRow, TeachersMode } from './types';

/*
 * Client-side row filters. Applied in the browser rather than on the server so
 * they are instant and never invalidate the cached report.
 */

/** True when a strip has at least one active day (or, for a band, any activity). */
export const hasActivity = (strip: number[]): boolean => strip.some((count) => count > 0);

/** A row's data-empty attribute: set when it has only zeros, so the grid grays it out. */
export const emptyRowAttr = (counts: number[]): 'true' | undefined =>
  hasActivity(counts) ? undefined : 'true';

/**
 * "Active teachers" drops teacher rows with no active day in the window.
 * School rows stay, because a school where nobody was active is a finding in
 * itself. Only "Hide rows with no active days" hides them.
 */
export const filterInactiveTeachers = (
  rows: ReportRow[],
  mode: TeachersMode,
): ReportRow[] => {
  if (mode === 'all') {
    return rows;
  }
  return rows.filter((row) => row.type !== 'teacher' || hasActivity(row.strip));
};

/**
 * "Hide rows with no active days" drops every school, teacher and section row
 * whose strip is all zeros. The district row stays as the root of the grid. A
 * school is active whenever one of its teachers is, so no active row loses its
 * parent. Activity rows are never filtered here, so a teacher's zeros stay.
 */
export const filterEmptyRows = (rows: ReportRow[], mode: EmptyRowsMode): ReportRow[] => {
  if (mode === 'show') {
    return rows;
  }
  return rows.filter((row) => row.type === 'district' || hasActivity(row.strip));
};

/**
 * Teacher name search. A matched teacher's ancestors are kept, otherwise the
 * row would have no school to hang from and the hierarchy would render orphans.
 */
export const filterRowsByTeacher = (rows: ReportRow[], search: string): ReportRow[] => {
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return rows;
  }

  const matched = rows.filter(
    (row) => row.type === 'teacher' && row.name.toLowerCase().includes(needle),
  );
  const keep = new Set<string>();
  const byKey = new Map(rows.map((row) => [row.key, row]));

  matched.forEach((row) => {
    keep.add(row.key);
    let parentKey = row.parent;
    while (parentKey) {
      keep.add(parentKey);
      const parent = byKey.get(parentKey);
      parentKey = parent ? parent.parent : null;
    }
  });

  return rows.filter((row) => keep.has(row.key));
};

/**
 * Ancestor keys (school, district, ...) of every currently-matched teacher --
 * NOT the matched teacher's own key, since visibility only depends on
 * ancestors being expanded (see AccountabilityGrid's `isVisible`).
 *
 * Used to force those ancestors open while a search is active, so a match is
 * actually reachable rather than hidden behind a collapsed school, without
 * permanently mutating the admin's own manual expand/collapse choices --
 * clearing the search must return to exactly what they had.
 */
export const matchedTeacherAncestorKeys = (
  rows: ReportRow[],
  search: string,
): string[] => {
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return [];
  }

  const byKey = new Map(rows.map((row) => [row.key, row]));
  const ancestors = new Set<string>();

  rows
    .filter((row) => row.type === 'teacher' && row.name.toLowerCase().includes(needle))
    .forEach((row) => {
      let parentKey = row.parent;
      while (parentKey) {
        ancestors.add(parentKey);
        const parent = byKey.get(parentKey);
        parentKey = parent ? parent.parent : null;
      }
    });

  return Array.from(ancestors);
};
